import React from 'react';
import { getLeaveTypeBadgeClass } from '../../utils/leaveTypeBadgeColors';
import {
  getDenseLeaveTypeAbbreviation,
  toCanonicalLeaveTypeName,
} from '../../utils/leaveDisplayUtils';

/**
 * Split a leave-type name into family + subtype when the parenthetical is a
 * real phrase, not a short code.
 *
 * Short codes — (PH), (AM), (PM), (NC), (Unpaid) — stay in the primary
 * string. HR reads "Paid Leave (PH)" as one name; stacking "(PH)" looked
 * like a layout bug next to single-line siblings.
 *
 * Long subtypes — Immediate Family, Grandparent, Non-Citizen, … — are a
 * second piece of information (different statutory entitlement). They
 * render inline as `{family} · {subtype}` so every leave chip keeps the
 * same pill height. The subtype is one step smaller, not micro-text:
 * it is the part an approver must actually read (3 days vs 1 day).
 *
 * Also accepts hyphen / en-dash / middle-dot separators so a display
 * string like "Compassionate Leave - Immediate Family" still splits.
 */
export function splitLeaveTypeLabel(leaveTypeName) {
  const trimmed = (leaveTypeName || '').trim();
  const parenMatch = /^(.+?)\s+\(([^)]+)\)$/.exec(trimmed);
  if (parenMatch) {
    const inner = parenMatch[2].trim();
    const isShortCode = inner.length <= 6 && !/\s/.test(inner);
    if (isShortCode) return { primary: trimmed, secondary: null };
    return { primary: parenMatch[1].trim(), secondary: inner };
  }

  const sepMatch = /^(.+?)\s+[·–—-]\s+(.+)$/.exec(trimmed);
  if (sepMatch) {
    const inner = sepMatch[2].trim();
    const isShortCode = inner.length <= 6 && !/\s/.test(inner);
    if (!isShortCode) return { primary: sepMatch[1].trim(), secondary: inner };
  }

  return { primary: trimmed, secondary: null };
}

/**
 * Leave Type Badge component.
 *
 * The ONLY way to render a color-coded leave type chip in the HR Portal.
 * Do NOT use inline Tailwind for leave-type colors or create local helpers.
 *
 * Every chip is a single-line pill. Long subtypes append
 * ` · {subtype}` at one step smaller (`text-[10px]` / `text-[9px]`).
 * Short codes stay inside the primary label. Never stack; never drop
 * the subtype below 9px — that is the legally distinct entitlement.
 *
 * Context: Leave Management module (applications, calendar, modals, reports)
 * and Attendance leave/arrangement chips (via getLeaveTypeBadgeClass).
 *
 * @param {string}  leaveTypeName - Display name (e.g. 'Annual Leave', 'Outpatient Sick Leave')
 * @param {string}  [size]        - 'xs' for mobile/compact, 'sm' (default) for tables
 * @param {boolean} [compact]     - Dense tables: official abbreviation when the
 *   full compound name would overflow a fixed column (CL(IF), CCL(NC), …).
 *   Hover/title still shows the official full name.
 */
export const LeaveTypeBadge = ({ leaveTypeName, size, compact = false }) => {
  if (!leaveTypeName) return null;

  const colorClasses = getLeaveTypeBadgeClass(leaveTypeName);
  const officialName = toCanonicalLeaveTypeName(leaveTypeName) || leaveTypeName;
  const denseAbbr = compact ? getDenseLeaveTypeAbbreviation(leaveTypeName) : null;
  const { primary, secondary } = splitLeaveTypeLabel(leaveTypeName);
  const isXs = size === 'xs';
  const sizeClasses = isXs ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]';
  const qualifierSize = isXs ? 'text-[9px]' : 'text-[10px]';

  return (
    <span
      className={`inline-flex items-center font-medium not-italic ring-1 ring-inset whitespace-nowrap rounded-full ${denseAbbr ? 'flex-shrink-0' : ''} ${colorClasses} ${sizeClasses}`}
      title={officialName}
      {...(denseAbbr ? { 'aria-label': officialName } : {})}
    >
      {denseAbbr ? (
        <span className="whitespace-nowrap">{denseAbbr}</span>
      ) : (
        <>
          <span className="whitespace-nowrap">{primary}</span>
          {secondary && (
            <>
              <span className="mx-0.5 font-normal opacity-70" aria-hidden="true">·</span>
              <span className={`${qualifierSize} font-normal leading-none whitespace-nowrap opacity-80`}>
                {secondary}
              </span>
            </>
          )}
        </>
      )}
    </span>
  );
};

export default LeaveTypeBadge;
